// 暫定距離の自動計算（2026-08-28 オーナー決定: Google Routes API）— 純ロジック。
// supabase・fetch・DOM に依存しない。テストは test_route_distance_core.mjs。
// 設計: engineering-タクミ/docs/2026-08-28-暫定距離-設計.md

const norm = (v) => String(v == null ? '' : v).trim()

// 地点 {pref, city} → 住所文字列（都道府県+市区町村）。どちらか欠けたら null。
export function placeAddr(p) {
  const pref = norm(p && p.pref), city = norm(p && p.city)
  return pref && city ? pref + city : null
}

// stops（訪問順）→ {origin, dest}。origin＝最初の積地・dest＝最後の卸地。
// 両方に pref+city が揃っていなければ null（＝暫定距離は出さない）。
export function routeEndpoints(stops) {
  const arr = Array.isArray(stops) ? stops : []
  const picks = arr.filter((s) => s && s.role === 'pick')
  const drops = arr.filter((s) => s && s.role === 'drop')
  const o = picks[0], d = drops[drops.length - 1]
  if (!o || !d || !placeAddr(o) || !placeAddr(d)) return null
  return {
    origin: { pref: norm(o.pref), city: norm(o.city) },
    dest: { pref: norm(d.pref), city: norm(d.city) },
  }
}

// 市→市ペアの正規化キー: `pref+city|pref+city`（キャッシュのキー。空白は除去）。
export function pairKey(origin, dest) {
  const a = placeAddr(origin), b = placeAddr(dest)
  return a && b ? `${a}|${b}` : null
}

// プリフィルしてよいか。条件: 自社便・サインイン済（デモは呼ばない）・距離欄が空・端点あり。
// 手入力済み（距離欄に何か入っている）なら絶対に取得しない＝上書きしない。
// 戻り値: 取得してよければ端点 {origin, dest}、だめなら null。
export function shouldAutoKm({ kind, online, quoteKm, stops }) {
  if (kind !== 'own' || !online) return null
  if (norm(quoteKm) !== '') return null
  return routeEndpoints(stops)
}

// キャッシュ優先の取得器（依存注入でテスト可能）。距離 km に加え所要時間 min（分）も扱う（2026-08-28）。
// deps: { loadPairs(): Promise<{key:{km,min?}}|null>, savePair(key, {km,min}): Promise,
//         fetchRoute(origin, dest): Promise<{km,min}> }
//  - メモリ → 保存済みキャッシュ（settings/routecache）→ API の順。ヒット時は fetchRoute を呼ばない（無料枠防衛）
//  - **後方互換**: 旧形式のキャッシュ（km のみ・PR #60 以前）は壊さないが、時間が無いので
//    ヒット扱いにせず再取得して min を埋め直す（1ペア1回だけ余分に呼ぶ。以後は新形式でヒット）
//  - 同一キーの在飛行呼び出しは1本にまとめる（連打でも Google は1回）
//  - 失敗はメモリに焼き込まない（次の呼び出しで再試行できる）。savePair の失敗は握りつぶす
export function createRouteKmGetter(deps) {
  const mem = new Map()      // key -> {km, min}（成功のみ）
  const inflight = new Map() // key -> Promise
  return async function getRouteKm(origin, dest) {
    const key = pairKey(origin, dest)
    if (!key) throw new Error('地点（都道府県＋市区町村）が不足しています')
    if (mem.has(key)) return { ...mem.get(key), source: 'cache' }
    if (inflight.has(key)) return inflight.get(key)
    const p = (async () => {
      const pairs = await deps.loadPairs().catch(() => null)
      const c = pairs && pairs[key]
      const ckm = Number(c && c.km), cmin = Number(c && c.min)
      if (ckm > 0 && cmin > 0) {
        const val = { km: ckm, min: cmin }
        mem.set(key, val)
        return { ...val, source: 'cache' }
      }
      const r = await deps.fetchRoute(origin, dest)
      const km = Number(r && r.km), min = Number(r && r.min)
      if (!(km > 0)) throw new Error('距離を取得できませんでした')
      const val = { km, min: min > 0 ? min : null } // min はAPI都合で欠けうる（その時は率フォールバック）
      mem.set(key, val)
      try { await deps.savePair(key, val) } catch (e) { /* キャッシュ保存失敗は無視（次回また取得すればよい） */ }
      return { ...val, source: 'api' }
    })().finally(() => inflight.delete(key))
    inflight.set(key, p)
    return p
  }
}
